import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  QrCodeIcon,
  ClipboardDocumentIcon,
  CheckCircleIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { employeeAttendanceAPI } from '../../services/api';
import { QRCodeCanvas } from 'qrcode.react';

const AttendanceManagement = () => {
  const [copied, setCopied] = useState(false);

  // Get or create QR code
  const { data: qrData, isLoading, error } = useQuery({
    queryKey: ['attendance-qr'],
    queryFn: () => employeeAttendanceAPI.getOrCreateQR().then(res => res.data),
  });

  const attendanceUrl = qrData?.qr_code_id
    ? `${window.location.origin}/employee-attendance/${qrData.qr_code_id}`
    : '';

  const copyToClipboard = () => {
    if (attendanceUrl) {
      navigator.clipboard.writeText(attendanceUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadQR = () => {
    const canvas = document.getElementById('qr-code-canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'employee-attendance-qr.png';
      link.href = url;
      link.click();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
        <p className="text-red-300">Error loading attendance QR code: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Employee Attendance</h1>
        <p className="text-gray-300 mt-1 sm:mt-2 text-sm sm:text-base">
          Manage employee attendance with QR code
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* QR Code Card */}
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
          <div className="flex items-center mb-4">
            <QrCodeIcon className="h-6 w-6 text-blue-500 mr-2" />
            <h2 className="text-xl font-semibold text-white">Attendance QR Code</h2>
          </div>

          <div className="bg-white p-6 rounded-lg mb-4 flex justify-center">
            <QRCodeCanvas
              id="qr-code-canvas"
              value={attendanceUrl}
              size={256}
              level="H"
              includeMargin={true}
            />
          </div>

          <div className="space-y-3">
            <button
              onClick={copyToClipboard}
              className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              {copied ? (
                <>
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <ClipboardDocumentIcon className="h-5 w-5 mr-2" />
                  Copy Attendance Link
                </>
              )}
            </button>

            <button
              onClick={downloadQR}
              className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              Download QR Code
            </button>
          </div>

          <div className="mt-4 p-4 bg-gray-700 rounded-lg">
            <p className="text-sm text-gray-300 mb-2">Attendance URL:</p>
            <p className="text-xs text-gray-400 break-all font-mono">{attendanceUrl}</p>
          </div>

          <div className="mt-4 p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
            <p className="text-sm text-blue-300">
              <strong>Note:</strong> This is a permanent QR code. Employees can use it daily to mark their attendance.
            </p>
          </div>
        </div>

        {/* Instructions Card */}
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">How It Works</h2>

          <div className="space-y-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-600 text-white font-bold">
                  1
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-white">Share QR Code</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Share the QR code or link with your employees. They can save it for daily use.
                </p>
              </div>
            </div>

            <div className="flex">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-600 text-white font-bold">
                  2
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-white">Employee Check-In</h3>
                <p className="text-sm text-gray-400 mt-1">
                  Employees scan the QR code or open the link, enter their phone number, and check in.
                </p>
              </div>
            </div>

            <div className="flex">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-600 text-white font-bold">
                  3
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-white">Employee Check-Out</h3>
                <p className="text-sm text-gray-400 mt-1">
                  At the end of the day, employees check out and fill in what they did before and after lunch.
                </p>
              </div>
            </div>

            <div className="flex">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-600 text-white font-bold">
                  4
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-white">Track Attendance</h3>
                <p className="text-sm text-gray-400 mt-1">
                  View all attendance records, filter by date or employee.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-700">
            <Link
              to="/employee-attendance/records"
              className="w-full flex items-center justify-center px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
            >
              <CalendarIcon className="h-5 w-5 mr-2" />
              View Attendance Records
            </Link>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/employees/new"
            className="p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-center"
          >
            <p className="text-white font-medium">Add Employee</p>
            <p className="text-sm text-gray-400 mt-1">Create new employee</p>
          </Link>

          <Link
            to="/employees"
            className="p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-center"
          >
            <p className="text-white font-medium">Manage Employees</p>
            <p className="text-sm text-gray-400 mt-1">View all employees</p>
          </Link>

          <Link
            to="/employee-attendance/records"
            className="p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-center"
          >
            <p className="text-white font-medium">Attendance Reports</p>
            <p className="text-sm text-gray-400 mt-1">View detailed records</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AttendanceManagement;
